import { CONSENT_DECLINED_NAME_PLACEHOLDER } from '@/domain/entities/FakeLead';

/**
 * Entidade de domínio que generaliza `FakeLeadInput` (HUB-237, CA-20) para
 * cobrir tanto a captura fictícia do piloto (HUB-147) quanto a captura real
 * de um plugin (Quiz/Roleta, HUB-26/27) — `phone`/`cpf`/`customFields` só
 * passam a existir de fato a partir desta fatia, quando `CapturePluginLeadUseCase`
 * traduz o `LeadData` recebido de `onLeadCapture` (contrato `@hub/plugin-sdk`,
 * ADR-004) para este formato antes de enfileirar.
 *
 * `FakeLeadInput` é estruturalmente compatível com esta interface (todos os
 * campos extras são opcionais) — `EnqueueLeadUseCase`/`createQueuedLead`
 * aceitam ambos sem qualquer conversão explícita no caminho fake.
 */
export interface LeadCaptureInput {
  readonly name: string;
  readonly email: string;
  readonly phone?: string | null;
  readonly cpf?: string | null;
  readonly customFields?: Record<string, unknown>;
  readonly consentAccepted: boolean;
}

/**
 * Minimização de dados (LGPD, HUB-201/HUB-237 CA-18/CA-20): quando o
 * consentimento é recusado, **todos** os campos pessoais eventualmente
 * preenchidos (`name`/`email`/`phone`/`cpf`/`customFields`) são substituídos
 * por um placeholder não-pessoal/vazio antes de qualquer persistência ou
 * sincronização — generalização de `toWireSafeFakeLeadInput` (HUB-147), que
 * cobria apenas `name`/`email`. A captura em si (interação com o plugin)
 * nunca é bloqueada por isso (CA-18) — apenas os dados nunca chegam à rede.
 */
export function toWireSafeLeadCaptureInput(input: LeadCaptureInput): LeadCaptureInput {
  if (input.consentAccepted) return input;
  return {
    name: CONSENT_DECLINED_NAME_PLACEHOLDER,
    email: '',
    phone: null,
    cpf: null,
    customFields: {},
    consentAccepted: false,
  };
}
